<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import MessageThread  from '@/Components/Inbox/MessageThread.vue'
import Composer       from '@/Components/Inbox/Composer.vue'
import StatusDropdown from '@/Components/Inbox/StatusDropdown.vue'
import AssignMenu     from '@/Components/Inbox/AssignMenu.vue'
import ChannelIcon    from '@/Components/Inbox/ChannelIcon.vue'
import ContextDrawer  from '@/Components/Inbox/ContextDrawer.vue'
import Icon           from '@/Components/UI/Icon.vue'
import UiButton       from '@/Components/UI/UiButton.vue'
import { useInboxStore } from '@/stores/useInboxStore'
import { router } from '@inertiajs/vue3'

defineOptions({ layout: AppLayout })

const props = defineProps({
  selected:          { type: Object, required: true },
  assignable_users:  { type: Array,  default: () => [] },
})

const { t } = useI18n()
const store = useInboxStore()
const showDrawer = ref(false)

watch(
  () => props.selected,
  (sel) => store.hydrateFromProps({ selected: sel }),
  { immediate: true, deep: false },
)

const activeConversation = computed(() => store.selected || props.selected)

const onStatusChanged = () => router.reload({ only: ['selected'] })
const onAssigned = (detail) => {
  if (detail) store.setSelected(detail)
}

const takeThis = async () => {
  if (!activeConversation.value) return
  await store.takeConversation(activeConversation.value.id)
}
const releaseThis = async () => {
  if (!activeConversation.value) return
  await store.releaseConversation(activeConversation.value.id)
}
</script>

<template>
  <PageShell :title="activeConversation.customer_name || activeConversation.contact_name || t('inbox.thread.unmatched')"
             back-href="/inbox">
    <section class="flex flex-col h-[calc(100vh-150px)] border border-[color:var(--line)] rounded-[var(--radius-lg)] overflow-hidden bg-[color:var(--surface)]">
      <header class="flex items-center gap-3 p-3 border-b border-[color:var(--line)]">
        <ChannelIcon :channel-type="activeConversation.channel_type" :size="32" />
        <div class="flex-1 min-w-0">
          <p class="text-xs text-[color:var(--muted)] truncate">
            {{ activeConversation.customer_id ? t('inbox.thread.customer') : t('inbox.thread.unmatched') }}
          </p>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center h-9 w-9 rounded-[var(--radius-button)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--inkSoft)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surfaceAlt)]"
          :title="t('inbox.drawer.unmatched')"
          @click="showDrawer = true"
        >
          <Icon name="users" :size="16" />
        </button>
        <UiButton
          v-if="!activeConversation.taken_at"
          variant="primary"
          size="sm"
          @click="takeThis"
        >
          {{ t('inbox.thread.takeThis') }}
        </UiButton>
        <UiButton
          v-else-if="activeConversation.taken_at"
          variant="ghost"
          size="sm"
          @click="releaseThis"
        >
          {{ t('inbox.thread.release') }}
        </UiButton>
        <AssignMenu
          v-if="activeConversation.taken_at"
          :conversation-id="activeConversation.id"
          :taken-by-id="activeConversation.taken_by_id"
          :users="props.assignable_users"
          @changed="onAssigned"
        />
        <span
          v-if="activeConversation.taken_by_name && activeConversation.taken_at"
          class="text-xs text-[color:var(--muted)]"
        >
          {{ t('inbox.thread.takenBy', { name: activeConversation.taken_by_name }) }}
        </span>
        <StatusDropdown
          :conversation-id="activeConversation.id"
          :status="activeConversation.status"
          :pipeline-stage="activeConversation.pipeline_stage"
          :taken="!!activeConversation.taken_at"
          @changed="onStatusChanged"
        />
      </header>
      <MessageThread :messages="activeConversation.messages" />
      <Composer
        :conversation-id="activeConversation.id"
        :taken="!!activeConversation.taken_at"
      />
    </section>

    <Teleport to="body">
      <Transition name="overlay">
        <div
          v-if="showDrawer"
          class="fixed inset-0 z-50 flex md:items-center md:justify-end md:p-4 bg-black/60"
          @click.self="showDrawer = false"
        >
          <div class="w-full h-full md:w-80 md:h-auto md:max-h-[calc(100vh-2rem)] md:rounded-[var(--radius-lg)]
                      bg-[color:var(--surface)] flex flex-col overflow-hidden">
            <header class="flex items-center justify-between p-3 border-b border-[color:var(--line)]">
              <span class="text-sm fw-value">{{ activeConversation.customer_name || activeConversation.contact_name || t('inbox.thread.unmatched') }}</span>
              <button type="button" class="text-[color:var(--muted)] hover:text-[color:var(--ink)]" @click="showDrawer = false">
                <Icon name="x" :size="16" />
              </button>
            </header>
            <ContextDrawer :conversation="activeConversation" class="flex-1 border-0" />
          </div>
        </div>
      </Transition>
    </Teleport>
  </PageShell>
</template>
